import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, ChevronRight, TrendingUp, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const scoreColors = (score) => {
  if (score >= 80) return { bg: "from-green-500 to-emerald-600", chip: "bg-green-100 text-green-700" };
  if (score >= 50) return { bg: "from-amber-500 to-orange-500", chip: "bg-amber-100 text-amber-700" };
  return { bg: "from-slate-400 to-slate-500", chip: "bg-slate-100 text-slate-600" };
};

/**
 * Dashboard widget: Test Readiness Score (0-100) with label and 3 weakest topics.
 * Free users see an upgrade nudge.
 */
const ReadinessWidget = ({ category, isPremium = false }) => {
  const { t } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.getReadiness(category);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category]);

  if (loading) {
    return (
      <div className="card-pop animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
        <div className="h-24 bg-slate-200 rounded" />
      </div>
    );
  }
  if (!data) return null;

  const label =
    data.label === "Ready"
      ? t("readiness_ready")
      : data.label === "Almost Ready"
      ? t("readiness_almost")
      : t("readiness_not");
  const c = scoreColors(data.score);

  return (
    <div className="card-pop" data-testid="readiness-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-indigo-500" />
          <h2 className="font-heading font-bold text-lg text-slate-800">
            {t("readiness_title")}
          </h2>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold ${c.chip}`}
          data-testid="readiness-label"
        >
          {label}
        </span>
      </div>

      {!isPremium && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <Crown className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            Free preview — upgrade for a full weekly readiness report and topic-by-topic plan.
          </p>
        </div>
      )}

      {/* Score circle */}
      <div className="flex items-center gap-6">
        <div
          className={`relative w-28 h-28 rounded-full bg-gradient-to-br ${c.bg} flex items-center justify-center shadow-lg`}
          data-testid="readiness-score"
        >
          <div className="absolute inset-2 bg-white rounded-full flex flex-col items-center justify-center">
            <span className="font-heading font-black text-3xl text-slate-800">
              {data.score}%
            </span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">
              score
            </span>
          </div>
        </div>
        <div className="flex-1">
          {!data.has_data ? (
            <p className="text-sm text-slate-500">{t("readiness_no_data")}</p>
          ) : (
            <p className="text-sm text-slate-500">
              <span className="font-bold text-slate-700">
                {data.topics_mastered}/{data.topics_total}
              </span>{" "}
              topics mastered • {data.accuracy}% overall accuracy
            </p>
          )}
        </div>
      </div>

      {/* Weak topics */}
      {data.weak_topics && data.weak_topics.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {t("readiness_weak_topics")}
          </p>
          <ul className="space-y-2">
            {data.weak_topics.map((w) => (
              <li
                key={w.topic}
                className="flex items-center justify-between p-2 rounded-xl bg-slate-50 hover:bg-indigo-50 transition-colors"
              >
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-700">{w.topic}</p>
                  {w.total > 0 ? (
                    <p className="text-xs text-slate-400">
                      {w.correct}/{w.total} correct • {w.accuracy}%
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400">Not started yet</p>
                  )}
                </div>
                <Link
                  to={`/lessons?category=${data.category}&topic=${encodeURIComponent(w.topic)}`}
                  className="flex items-center gap-1 px-3 py-1 bg-indigo-500 text-white rounded-full text-xs font-bold hover:bg-indigo-600"
                  data-testid={`readiness-lesson-${w.topic}`}
                >
                  Practice
                  <ChevronRight className="w-3 h-3" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ReadinessWidget;
